import { ReactNode } from "react";

import { cx } from "class-variance-authority";

import { Modal, ModalWidth } from "../Modal";

import { DialogIcon, dialogIcons } from "./dialogIcons";
import * as styles from "./InformationModal.css";

export type InformationModalType = "info" | "success" | "warning" | "error";

export interface InformationModalProps {
    open: boolean;
    onClose: () => void;
    /** Sets the icon's tint, and its glyph when `icon` doesn't. */
    type: InformationModalType;
    /**
     * The glyph to lead with. `info` dialogs name the action they confirm
     * (`archive`, `reminder`, …) rather than showing a generic info circle.
     */
    icon?: DialogIcon;
    title: ReactNode;
    /** The message body, indented to line up with the title. */
    children?: ReactNode;
    /** Buttons for the bottom right action row. */
    footer?: ReactNode;
    /**
     * Numbers are pixels; percentages are relative to the viewport (e.g.
     * `"80%"`).
     */
    width?: ModalWidth;
    className?: string;
}

// Narrower than a plain dialog: this only ever holds a line or two of message.
const DEFAULT_WIDTH = 440;

/**
 * A modal that leads with an icon — the design's "Pop-up / Information". It has
 * no close button: the action row is the way out.
 */
export function InformationModal({
    open,
    onClose,
    type,
    icon,
    title,
    children,
    footer,
    width = DEFAULT_WIDTH,
    className,
}: InformationModalProps) {
    return (
        <Modal
            variant="dialog"
            open={open}
            onClose={onClose}
            hideCloseButton
            disableFullscreen
            width={width}
            footer={footer}
            className={cx(styles.card, className)}
            bodyClassName={styles.message}
            title={title}
            icon={
                <span className={styles.iconType[type]}>
                    {dialogIcons[icon ?? defaultIcons[type]]}
                </span>
            }
        >
            {children}
        </Modal>
    );
}

const defaultIcons: Record<InformationModalType, DialogIcon> = {
    info: "info",
    success: "success",
    warning: "failure",
    error: "delete",
};
